import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import "yet-another-react-lightbox/styles.css";
import profile from "../images/chenyu-portrait.jpg";
import TextScramble, { ScrambleScope } from "./TextScramble";
import allProjectsData from "../data/projects_updated.json";
// import { ProjectCards3D } from "./ProjectCards3D";
// import { ProjectCardsCarousel } from "./ProjectCardsCarousel";

const newsItems = [
  {
    outlet: "Tech Times",
    date: "March 23, 2026",
    description:
      "Tech Times featured Chenyu Zhang's work on emotion-aware AI tutors, highlighting how affective computing and large language models can personalize learning and advance educational equity.",
    href: "https://www.techtimes.com/articles/315365/20260323/emotion-strength-how-chenyu-zhang-redefining-future-ai-tutors.htm",
    linkLabel: "Article",
  },
  {
    outlet: "Guardian Nigeria",
    date: "March 19, 2026",
    description:
      "Guardian Nigeria covered how Chenyu Zhang's research and startup work explore AI tutors that can notice frustration, curiosity, and confidence as part of the learning process.",
    href: "https://guardian.ng/technology/tech/when-ai-understands-feelings-redefining-how-students-learn-in-the-age-of-generative-ai/",
    linkLabel: "Article",
  },
  {
    outlet: "TechBullion",
    date: "March 11, 2026",
    description:
      "TechBullion examined Chenyu Zhang's work at the intersection of emotion-aware AI, education, and human-centered tutoring, with a focus on building more humane learning technology.",
    href: "https://techbullion.com/teaching-machines-to-feel-chenyu-zhang-on-emotion-intelligence-and-the-future-of-learning/",
    linkLabel: "Article",
  },
  {
    outlet: "Business Insider",
    date: "September 24, 2025",
    description:
      "GlowingStar today announced the development of the world's first affective AI tutor, an emotion-aware learning coach designed to adapt to both cognition and emotion while making its reasoning transparent to educators. The launch positions GlowingStar founder Chenyu Zhang, a recent Harvard graduate, to compete directly with OpenAI's newly released study mode.",
    href: "https://markets.businessinsider.com/news/stocks/glowingstar-announces-development-of-world-s-first-affective-ai-tutor-1035198941",
    linkLabel: "Article",
  },
  {
    outlet: "HGSE Alumni",
    date: "2025",
    description:
      "Harvard Graduate School of Education Alumni Newsletter featured Chenyu Zhang, Ed.M. '25 in the Alumni Spotlights section. The spotlight highlighted his work as founder of GlowingStar.AI, building emotion-aware AI tutors for personalized learner support. The feature also noted his research at the MIT Media Lab, Stanford HAI, and the Berkman Klein Center, with publications having a growing influence in AI and education research. His teaching impact supporting learners at MIT, Stanford's Code in Place, and other institutions was also recognized.",
    href: "https://view.hu.harvard.edu/?qs=e43b9b0c150d149a63c563a143d76acb2cb678bf17de9a2388b588cfa89fdfbb15e32e667316afa8ae4bdf4a09186c97df4d1a44b219752ac411f8f67f7cef4ca8fa0613e1a6abb784ad72d28b7d0f4847f777a13774c7ae",
    linkLabel: "Newsletter",
  },
  {
    outlet: "U of T News",
    date: "September 6, 2017",
    description:
      "University of Toronto News covered the launch of the computer science Second-Year Learning Communities, spotlighting peer mentors Jennifer Campbell, Jacqueline Smith, and Chenyu (Charlie) Zhang for helping students build community and navigate hackathons, careers, and inclusive learning experiences.",
    href: "https://www.utoronto.ca/news/uoftbacktoschool-computer-science-launches-second-year-learning-communities",
    linkLabel: "Article",
  },
];

// Styled Components
const MainContainer = styled.div`
  height: 100%;
  min-height: 100vh;
  background: #000000;
  color: white;
  position: relative;
  overflow-x: hidden;

  /* Enable smooth scrolling for the entire page */
  scroll-behavior: smooth;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }
  }
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 10rem;
  text-align: center;
  align-items: center;
  margin-bottom: 4rem;
  position: relative;

  @media (min-width: 600px) and (max-width: 960px) {
    padding-top: 11rem;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 200px;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.03) 0%,
      transparent 70%
    );
    border-radius: 50%;
    z-index: -1;
  }
`;

const ProfileImage = styled.img`
  border-radius: 8px;
  width: 160px;
  height: 160px;
  object-fit: cover;
  margin-bottom: 1rem;
  border: 1px solid rgba(247, 247, 248, 0.1);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(247, 247, 248, 0.2);
    transform: scale(1.02);
  }
`;

const NavigationBar = styled(AppBar)`
  background: rgba(0, 0, 0, 0.9) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: fixed !important;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  min-height: 64px;

  @media (max-width: 600px) {
    min-height: 56px;
  }

  @media (min-width: 600px) and (max-width: 960px) {
    min-height: 80px;
  }

  @media (min-width: 960px) {
    min-height: 72px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.95) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  }

  .MuiToolbar-root {
    min-height: inherit;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (max-width: 600px) {
      padding: 0 12px;
    }

    @media (min-width: 960px) {
      padding: 0 24px;
    }
  }
`;

const HeroSection = styled(ScrambleScope)`
  min-height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 80px;
  padding-bottom: 2rem;
  overflow: hidden;
  background: #000000;

  @media (max-width: 600px) {
    padding-top: 70px;
  }

  @media (min-width: 600px) and (max-width: 960px) {
    padding-top: 100px;
  }

  @media (min-width: 960px) {
    padding-top: 90px;
  }
`;

const HeroContentWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 0;
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1200px;
  width: 100%;
  padding: 5rem 2rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const HeroPhoto = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HeroImage = styled.img`
  width: 400px;
  height: 500px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    width: 300px;
    height: 375px;
  }
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const HeroTitle = styled.h1`
  font-size: clamp(3.25rem, 7vw, 5rem);
  font-weight: 400;
  color: #ffffff;
  margin: 0;
  letter-spacing: 0;
  line-height: 1.04;
  font-family: var(--spark-serif, serif);
  text-shadow: 0 8px 22px rgba(0, 0, 0, 0.5);

  .dot {
    color: var(--spark-accent, #2b6be4);
  }
`;

const HeroSubtitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const HeroSubtitleLine = styled.p`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-weight: 300;
  line-height: 1.4;
  font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const KeywordList = styled.div`
  font-size: 1.375rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  font-weight: 600;
  line-height: 1.45;
  font-family: var(--spark-sans, sans-serif);
  letter-spacing: -0.01em;
  max-width: 38ch;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  gap: 0.5em;

  /* ditto's angel wings flanking the tagline (spark's .rp-l-winged) */
  &::before,
  &::after {
    content: "";
    flex: none;
    width: 2.2em;
    height: 0.85em;
    background: currentColor;
    opacity: 0.9;
    -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 126 48'%3E%3Cpath d='M2 40C30 40 60 35 86 22 100 15 112 13 124 16 112 20 104 26 104 26 114 25 121 29 124 36 112 33 103 37 103 37 112 38 118 41 122 46 102 40 78 41 58 40 38 39 18 40 2 42Z'/%3E%3C/svg%3E")
      center / contain no-repeat;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 126 48'%3E%3Cpath d='M2 40C30 40 60 35 86 22 100 15 112 13 124 16 112 20 104 26 104 26 114 25 121 29 124 36 112 33 103 37 103 37 112 38 118 41 122 46 102 40 78 41 58 40 38 39 18 40 2 42Z'/%3E%3C/svg%3E")
      center / contain no-repeat;
  }

  &::before {
    transform: scaleX(-1);
  }

  @media (max-width: 768px) {
    font-size: 1.125rem;
    justify-content: center;
  }

  .bold {
    font-weight: 600;
  }
`;

const CredibilityCarousel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin: 2rem 0;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    animation: shimmer 3s infinite;
  }

  @keyframes shimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }

  @media (max-width: 768px) {
    gap: 1.5rem;
    padding: 1rem;
    margin: 1.5rem 0;
  }
`;

const CredibilityLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  animation: float 6s ease-in-out infinite;
  animation-delay: ${(props) => props.delay}s;

  &:hover {
    transform: translateY(-5px) scale(1.05);
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 8px 25px rgba(255, 255, 255, 0.15);
  }

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
    filter: brightness(0) invert(1);
    opacity: 0.9;
    transition: all 0.3s ease;
  }

  &:hover img {
    opacity: 1;
    filter: brightness(0) invert(1)
      drop-shadow(0 0 8px rgba(255, 255, 255, 0.3));
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;

    img {
      width: 32px;
      height: 32px;
    }
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-8px);
    }
  }
`;

const CredibilityLabel = styled.div`
  position: absolute;
  bottom: -2.5rem;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 400;
  text-align: center;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;

  ${CredibilityLogo}:hover & {
    opacity: 1;
  }
`;

const MiniTagline = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  font-weight: 300;
  line-height: 1.4;
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
  font-style: italic;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ScrollIndicator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  margin-top: auto;
  padding: 1rem 0 2rem 0;
  animation: bounce 2s infinite;

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }

  &::after {
    content: "↓";
    font-size: 1.5rem;
    margin-top: 0.5rem;
  }
`;

const ContactIcons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const ContactIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    color: rgba(255, 255, 255, 0.95);
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const CollaborationText = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.75);
  margin: 0.5rem 0 0.25rem 0;
  font-weight: 400;
  line-height: 1.6;
  font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: 0.9375rem;
    margin: 1.25rem 0 0.375rem 0;
    line-height: 1.55;
  }

  a {
    color: rgba(255, 255, 255, 0.95);
    text-decoration: underline;
    text-decoration-color: rgba(255, 255, 255, 0.3);
    transition: all 0.2s ease;
    font-weight: 500;

    &:hover {
      color: rgba(255, 255, 255, 1);
      text-decoration-color: rgba(255, 255, 255, 0.6);
    }
  }
`;

const ContactEmailOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0;
`;

const ContactEmailOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);

  span {
    font-weight: 500;
    min-width: 120px;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const ContactEmailLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  cursor: pointer;
  padding: 0;
  margin: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    color: rgba(255, 255, 255, 0.95);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const ContentSection = styled(ScrambleScope)`
  min-height: 80vh;
  padding: 4rem 2rem 3rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.8s ease forwards;

  @media (max-width: 768px) {
    padding: 3rem 1rem 2rem 1rem;
  }

  @keyframes fadeInUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: 3rem;
  margin-top: 2rem;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background: linear-gradient(
      180deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;

    &::before {
      display: none;
    }
  }
`;

const SectionHeading = styled.h3`
  font-size: 1rem;
  font-weight: 500;
  color: #a1a1aa;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;

  @media (max-width: 768px) {
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const ExperienceItem = styled.div`
  position: relative;
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  transition: border-bottom-color 0.2s ease;

  &:hover {
    border-bottom-color: rgba(255, 255, 255, 0.4);
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem 0;
  }
`;

const CompanyLogo = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.02) 0%,
      transparent 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.08);
    transform: scale(1.05);
    box-shadow: 0 4px 16px rgba(255, 255, 255, 0.1);

    &::before {
      opacity: 1;
    }
  }

  img {
    width: 32px;
    height: 32px;
    object-fit: contain;
    filter: brightness(0.9) contrast(1.1);
    transition: all 0.3s ease;
    position: relative;
    z-index: 1;
  }

  &:hover img {
    filter: brightness(1) contrast(1.2);
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;

    img {
      width: 28px;
      height: 28px;
    }
  }
`;

const ExperienceContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ExperienceTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 400;
  color: #f7f7f8;
  margin: 0 0 0.25rem 0;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display",
    "Helvetica Neue", sans-serif;
  letter-spacing: -0.01em;
`;

const ExperienceSubtitle = styled.div`
  font-size: 0.875rem;
  color: #a1a1aa;
  margin: 0 0 0.5rem 0;
  font-weight: 400;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ExperienceDescription = styled.p`
  font-size: 0.9375rem;
  color: #d1d5db;
  margin: 0;
  line-height: 1.5;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;
  font-weight: 400;
`;

const ExperienceLocation = styled.span`
  font-size: 0.875rem;
  color: #a1a1aa;
  font-weight: 400;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &::before {
    content: "•";
    margin-right: 0.5rem;
    opacity: 0.6;
  }

  &:hover {
    opacity: 1;
  }
`;

const PublicationStatus = styled.span`
  font-size: 0.875rem;
  font-weight: 400;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;
  margin-left: 0.75rem;
  transition: opacity 0.2s ease;
  color: #a1a1aa;
  opacity: 0.8;

  &::before {
    content: "•";
    margin-right: 0.5rem;
    opacity: 0.6;
  }
`;

const PublicationGroup = styled.div`
  margin-bottom: 3rem;
`;

const PublicationItem = styled.div`
  display: flex;
  gap: 1rem;
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  transition: border-bottom-color 0.2s ease;

  &:hover {
    border-bottom-color: rgba(255, 255, 255, 0.4);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    padding-left: 0;
    margin-left: 0;
  }
`;

const ConferenceColumn = styled.div`
  flex-shrink: 0;
  width: 180px;
  display: flex;
  align-items: flex-start;
  padding-top: 0.25rem;

  @media (max-width: 768px) {
    width: auto;
    margin-bottom: 0.5rem;
  }
`;

const ConferenceBadge = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    color: #f0f0f0;
  }
`;

const ProjectColumn = styled.div`
  flex-shrink: 0;
  width: 180px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-top: 0;

  @media (max-width: 768px) {
    width: auto;
    margin-bottom: 0.5rem;
    margin-left: 0;
    padding-left: 0.875rem;
  }
`;

const ProjectName = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #007bff;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;
  letter-spacing: 0.01em;
  line-height: 1.4;
  word-break: break-word;
  transition: color 0.2s ease;
  text-decoration: none;
  cursor: pointer;

  ${PublicationItem}:hover & {
    color: #5ac8fa;
  }

  &:hover,
  &:focus {
    text-decoration: underline;
    text-decoration-color: rgba(90, 200, 250, 0.5);
  }

  svg {
    width: 0.85rem;
    height: 0.85rem;
    opacity: 0.75;
    transition: opacity 0.2s ease;
  }

  &:hover svg,
  &:focus svg {
    opacity: 1;
  }
`;

const ProjectDate = styled.div`
  font-size: 0.8125rem;
  font-weight: 400;
  color: #a1a1aa;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;
  line-height: 1.3;
  word-break: break-word;
`;

const PublicationContent = styled.div`
  flex: 1;

  @media (max-width: 768px) {
    margin-left: 0;
    padding-left: 0;
  }
`;

const GroupTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 500;
  color: #f7f7f8;
  margin: 0 0 0 0;
  padding-bottom: 0.75rem;
  margin-bottom: 1.25rem;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display",
    "Helvetica Neue", sans-serif;
  letter-spacing: -0.01em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.875rem;
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 300;
  margin-bottom: 3rem;
  color: #f7f7f8;
  text-align: center;
  letter-spacing: -0.02em;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display",
    "Helvetica Neue", sans-serif;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -0.75rem;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 1px;
    background: #f7f7f8;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Paragraph = styled.div`
  margin-bottom: 1.5rem;
  margin-left: auto;
  margin-right: auto;
  max-width: 50rem;
  font-size: 1.125rem;
  line-height: 1.6;
  font-weight: 400;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;
  text-align: center;
  color: #d1d5db;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const Disclaimer = styled.div`
  font-style: italic;
  margin-top: 5rem;
  margin-left: auto;
  margin-right: auto;
  max-width: 50rem;
  font-size: 1rem;
  line-height: 1.6;
  font-weight: 400;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  opacity: 0.8;
`;

const HireMeLink = styled.a`
  color: #f7f7f8;
  font-weight: 500;
  font-size: 1.125rem;
  text-decoration: none;
  margin-left: 0.5em;
  transition: all 0.3s ease;
  cursor: pointer;
  border-bottom: 1px solid transparent;

  &:hover,
  &:focus {
    color: #d1d5db;
    border-bottom: 1px solid #a1a1aa;
  }
`;

const PaperLink = styled.a`
  color: #f7f7f8;
  text-decoration: underline;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  display: inline-block;

  &:hover,
  &:focus {
    color: #d1d5db;
    opacity: 0.9;
    transform: translateY(-1px);
    text-decoration-color: #a1a1aa;
  }
`;

const SectionLink = styled.a`
  color: #007bff;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  transition: color 0.2s ease;

  &:hover,
  &:focus {
    color: #5ac8fa;
    outline: none;
  }

  &:active {
    color: #4da6ff;
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover svg,
  &:focus svg {
    opacity: 1;
  }
`;

const ScrollProgressBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: ${(props) => props.progress}%;
  height: 2px;
  background: #f7f7f8;
  z-index: 1001;
  transition: width 0.3s ease;
`;

const ScrollToTopButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 48px;
  height: 48px;
  border-radius: 0;
  background: rgba(247, 247, 248, 0.05);
  border: 1px solid rgba(247, 247, 248, 0.2);
  color: #f7f7f8;
  font-size: 1.25rem;
  cursor: pointer;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: all 0.3s ease;
  z-index: 1000;

  &:hover {
    background: rgba(247, 247, 248, 0.1);
    border-color: rgba(247, 247, 248, 0.3);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    bottom: 1rem;
    right: 1rem;
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }
`;

const PortfolioImageContainer = styled.div`
  margin: 2rem 0;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    margin: 1.5rem 0;
  }
`;

const PortfolioImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin: 1.5rem 0;
  }
`;

const PortfolioImage = styled.img`
  width: 100%;
  max-width: 420px;
  aspect-ratio: 4 / 3;
  border-radius: 20px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.45);
  transition: box-shadow 0.3s ease;
  object-fit: contain;
  background: transparent;

  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 14px;
  }
`;

const PortfolioImageCaption = styled.p`
  margin-top: 0.75rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  letter-spacing: 0.01em;
`;

const PhotoAlbumContainer = styled.div`
  margin: 2rem 0;

  /* Ensure images maintain aspect ratio */
  img {
    border-radius: 20px;
    box-shadow: 0 18px 45px rgba(0, 0, 0, 0.45);
    transition: all 0.3s ease;
    cursor: pointer;
    object-fit: contain;
    display: block;

    &:hover {
      box-shadow: 0 18px 45px rgba(255, 255, 255, 0.1);
      transform: translateY(-2px);
    }
  }

  /* PhotoAlbum wrapper to preserve aspect ratios */
  > div {
    img {
      max-width: 100%;
      height: auto;
    }
  }

  @media (max-width: 768px) {
    margin: 1.5rem 0;

    img {
      border-radius: 14px;
    }
  }
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const VideoCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-4px);
    box-shadow: 0 8px 32px rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const VideoTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 500;
  color: #f7f7f8;
  margin: 0 0 0.75rem 0;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display",
    "Helvetica Neue", sans-serif;
  letter-spacing: -0.01em;
`;

const VideoDescription = styled.p`
  font-size: 0.9375rem;
  color: #d1d5db;
  margin: 0 0 1rem 0;
  line-height: 1.5;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;
  white-space: pre-line;
`;

const VideoIframe = styled.iframe`
  width: 100%;
  height: 215px;
  border-radius: 12px;
  border: none;
  margin-top: 0.5rem;
`;

const VideoElement = styled.video`
  width: 100%;
  height: 215px;
  border-radius: 12px;
  margin-top: 0.5rem;
  background-color: #000;
`;

// Compact project list styled components
const CompactProjectIntro = styled.p`
  margin: 0 0 1rem 0;
  font-size: 0.95rem;
  color: #d1d5db;
  line-height: 1.5;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;

  strong {
    color: #f7f7f8;
    font-weight: 700;
  }
`;

const CompactProjectList = styled.ul`
  margin: 0;
  padding-left: 0;
  color: #d1d5db;
  list-style: none;
`;

const CompactProjectItem = styled.li`
  margin: 0 0 0.35rem 0;
  padding-left: 0;
  font-size: 0.95rem;
  line-height: 1.45;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;
`;

const CompactProjectBullet = styled.span`
  color: #ffffff;
  display: inline-block;
  font-size: 1rem;
  line-height: 1;
  margin-right: 0.45rem;
`;

const CompactProjectIcon = styled.span`
  display: inline-block;
  margin-right: 0.3rem;
`;

const ProjectShortDescription = styled.span`
  margin: 0;
  font-size: 0.95rem;
  color: #d1d5db;
  line-height: 1.6;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text",
    "Helvetica Neue", sans-serif;
`;

const ProjectSummarySeparator = styled.span`
  color: #a1a1aa;
  font-size: 0.95rem;
`;

// Navigation pages
const pages = [
  "HOME",
  "BIO",
  "EDUCATION",
  "PUBLICATIONS",
  "REVIEWER",
  "WORK",
  "RESEARCH",
  "ENTREPRENEURSHIP",
  "TEACHING",
  "SPEAKING",
  "VOLUNTEERING",
  "COMMUNITY",
  "CERTIFICATIONS",
  "AWARDS",
  // "MUSIC",
  // "DANCE",
  // "SINGING",
  "NEWS",
  "PROJECTS",
];

// Helper function to format timestamp in human-readable format
const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const SinglePageApp = () => {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [activeSection, setActiveSection] = useState("HOME");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const navigate = useNavigate();

  // CSSSA Conference photos
  const csssaPhotos = [
    {
      src: "../images/portfolio-photos/cssa1.JPG",
      width: 800,
      height: 600,
      alt: "CSSSA Conference Photo 1",
    },
    {
      src: "../images/portfolio-photos/cssa2.jpg",
      width: 800,
      height: 600,
      alt: "CSSSA Conference Photo 2",
    },
    {
      src: "../images/portfolio-photos/cssa3.JPG",
      width: 800,
      height: 600,
      alt: "CSSSA Conference Photo 3",
    },
    {
      src: "../images/portfolio-photos/cssa4.JPG",
      width: 800,
      height: 600,
      alt: "CSSSA Conference Photo 4",
    },
  ];

  // Refs for each section
  const homeRef = useRef(null);
  const bioRef = useRef(null);
  const educationRef = useRef(null);
  const experienceRef = useRef(null);
  const entrepreneurshipRef = useRef(null);
  const researchRef = useRef(null);
  const teachingRef = useRef(null);
  const speakingRef = useRef(null);
  const volunteeringRef = useRef(null);
  const awardsRef = useRef(null);
  const publicationsRef = useRef(null);
  const newsRef = useRef(null);
  const communityRef = useRef(null);
  const certificationsRef = useRef(null);
  const reviewerRef = useRef(null);
  const musicRef = useRef(null);
  const danceRef = useRef(null);
  const singingRef = useRef(null);
  const projectsRef = useRef(null);

  const sectionRefs = {
    HOME: homeRef,
    BIO: bioRef,
    EDUCATION: educationRef,
    PUBLICATIONS: publicationsRef,
    REVIEWER: reviewerRef,
    WORK: experienceRef,
    RESEARCH: researchRef,
    ENTREPRENEURSHIP: entrepreneurshipRef,
    TEACHING: teachingRef,
    SPEAKING: speakingRef,
    VOLUNTEERING: volunteeringRef,
    COMMUNITY: communityRef,
    CERTIFICATIONS: certificationsRef,
    AWARDS: awardsRef,
    // MUSIC: musicRef,
    // DANCE: danceRef,
    // SINGING: singingRef,
    PROJECTS: projectsRef,
    NEWS: newsRef,
  };

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const scrollToSection = (sectionName) => {
    const ref = sectionRefs[sectionName];
    if (ref && ref.current) {
      // Update URL hash without triggering scroll
      const hash = sectionName.toLowerCase();
      window.history.pushState(null, "", `#${hash}`);

      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setActiveSection(sectionName);
    }
    handleCloseNavMenu();
  };

  const scrollToTop = () => {
    window.history.pushState(null, "", "#");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setActiveSection("HOME");
  };

  const navigateToDetailPage = (page) => {
    navigate(`/${page.toLowerCase()}`);
  };

  // Email protection function
  const handleEmailClick = (emailParts) => {
    const email = emailParts.join("@");
    window.open(`mailto:${email}`, "_blank");
  };

  // Handle initial hash navigation on mount
  useEffect(() => {
    const hash = window.location.hash.slice(1).toUpperCase();
    if (hash && sectionRefs[hash]) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (sectionRefs[hash].current) {
          sectionRefs[hash].current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          setActiveSection(hash);
        }
      }, 100);
    } else if (hash && !sectionRefs[hash]) {
      // Invalid hash, clear it
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1).toUpperCase();
      if (hash && sectionRefs[hash] && sectionRefs[hash].current) {
        sectionRefs[hash].current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        setActiveSection(hash);
      } else if (!hash) {
        // If no hash, scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
        setActiveSection("HOME");
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  // Intersection Observer to update active section and URL hash
  useEffect(() => {
    const observerOptions = {
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
      rootMargin: "-100px 0px -100px 0px",
    };

    // Track intersection state for all sections
    const sectionStates = new Map();

    const observer = new IntersectionObserver((entries) => {
      // Update the state for all entries in this callback
      entries.forEach((entry) => {
        const sectionName = Object.keys(sectionRefs).find(
          (key) => sectionRefs[key].current === entry.target
        );
        if (sectionName) {
          sectionStates.set(sectionName, {
            isIntersecting: entry.isIntersecting,
            ratio: entry.intersectionRatio,
            boundingTop: entry.boundingClientRect.top,
          });
        }
      });

      // Find all currently intersecting sections
      const intersectingSections = Array.from(sectionStates.entries())
        .filter(([_, state]) => state.isIntersecting)
        .map(([name, state]) => ({
          name,
          ratio: state.ratio,
          boundingTop: state.boundingTop,
        }));

      // If we have intersecting sections, select the one with highest ratio
      // If ratios are similar (within 0.05), prefer the one higher in viewport (lower boundingTop)
      if (intersectingSections.length > 0) {
        // Sort by intersection ratio (descending), then by position in viewport (ascending)
        intersectingSections.sort((a, b) => {
          const ratioDiff = b.ratio - a.ratio;
          // If ratios are very close (within 0.05), prefer the section higher in viewport
          if (Math.abs(ratioDiff) < 0.05) {
            return a.boundingTop - b.boundingTop;
          }
          return ratioDiff;
        });

        const activeSectionName = intersectingSections[0].name;
        setActiveSection(activeSectionName);
        // Update URL hash without triggering scroll (replaceState to avoid adding to history)
        const hash = activeSectionName.toLowerCase();
        if (window.location.hash.slice(1).toUpperCase() !== activeSectionName) {
          window.history.replaceState(null, "", `#${hash}`);
        }
      }
    }, observerOptions);

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, []);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <MainContainer>
      {/* spark jitter filters (stop-motion hand-drawn wobble) */}
      <svg
        width="0"
        height="0"
        style={{ position: "absolute" }}
        aria-hidden="true"
      >
        <defs>
          {[3, 10, 17, 24].map((seed, i) => (
            <filter
              id={`jitter-${i}`}
              key={i}
              x="-5%"
              y="-5%"
              width="110%"
              height="110%"
            >
              <feTurbulence
                type="turbulence"
                baseFrequency="0.04"
                numOctaves="3"
                seed={seed}
                result="noise"
              />
              <feDisplacementMap
                in="SourceGraphic"
                in2="noise"
                scale="3"
                xChannelSelector="R"
                yChannelSelector="G"
              />
            </filter>
          ))}
        </defs>
      </svg>

      {/* Scroll Progress Bar */}
      <ScrollProgressBar progress={scrollProgress} />

      {/* Fixed Navigation Bar */}
      <NavigationBar>
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: "inherit",
              padding: "0 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              "@media (max-width: 600px)": {
                padding: "0 12px",
                minHeight: "56px",
              },
              "@media (min-width: 600px) and (max-width: 960px)": {
                minHeight: "80px",
              },
              "@media (min-width: 960px)": {
                padding: "0 24px",
                minHeight: "72px",
              },
            }}
          >
            {/* Mobile Menu (Hamburger) */}
            <Box
              sx={{
                display: { xs: "flex" },
                "@media (min-width: 768px)": {
                  display: "none",
                },
              }}
            >
              <IconButton
                size="large"
                aria-label="navigation menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
                sx={{
                  padding: "12px",
                  color: "white",
                  margin: "8px 0",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: "block" },
                  "@media (min-width: 768px)": {
                    display: "none",
                  },
                  "& .MuiPaper-root": {
                    backgroundColor: "rgba(0, 0, 0, 0.95)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                    marginTop: "12px",
                    minWidth: "200px",
                    maxWidth: "280px",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                  },
                }}
                MenuListProps={{
                  sx: {
                    padding: "8px",
                  },
                }}
              >
                {pages.map((page) => (
                  <ScrambleScope
                    component={MenuItem}
                    key={page}
                    onClick={() => scrollToSection(page)}
                    sx={{
                      color: "white",
                      fontFamily:
                        "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif",
                      fontWeight: 500,
                      fontSize: "15px",
                      padding: "14px 16px",
                      borderRadius: "8px",
                      margin: "3px 0",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      position: "relative",
                      "&:hover": {
                        transition: "all 0.3s ease",
                        color: "#d1d5db",
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        transform: "translateX(4px)",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "rgba(255, 255, 255, 0.12)",
                        color: "#f7f7f8",
                        borderLeft: "3px solid #f7f7f8",
                      },
                    }}
                    selected={activeSection === page}
                  >
                    <Typography
                      textAlign="center"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TextScramble>{page}</TextScramble>
                    </Typography>
                  </ScrambleScope>
                ))}
              </Menu>
            </Box>

            {/* Tablet Navigation (Medium screens - 2 lines) */}
            <Box
              sx={{
                flexGrow: 1,
                justifyContent: "center",
                display: "none",
                "@media (min-width: 768px)": {
                  display: "flex",
                },
                "@media (min-width: 1200px)": {
                  display: "none",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  justifyContent: "center",
                  alignItems: "center",
                  maxWidth: "100%",
                  padding: "12px 0",
                }}
              >
                {/* First row */}
                <Box
                  sx={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {pages.slice(0, 6).map((page) => (
                    <ScrambleScope
                      component={Button}
                      key={page}
                      onClick={() => scrollToSection(page)}
                      sx={{
                        color: "white",
                        backgroundColor: "transparent",
                        display: "flex",
                        alignItems: "center",
                        fontSize: "13px",
                        padding: "8px 12px",
                        minWidth: "auto",
                        borderBottom:
                          activeSection === page
                            ? "2px solid #f7f7f8"
                            : "2px solid transparent",
                        transition: "all 0.3s ease",
                        fontFamily:
                          "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif",
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                        "&:hover": {
                          borderBottom: "2px solid #a1a1aa",
                          transform: "translateY(-1px)",
                          color: "#d1d5db",
                        },
                      }}
                    >
                      <TextScramble>{page}</TextScramble>
                    </ScrambleScope>
                  ))}
                </Box>

                {/* Second row */}
                <Box
                  sx={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {pages.slice(6).map((page) => (
                    <ScrambleScope
                      component={Button}
                      key={page}
                      onClick={() => scrollToSection(page)}
                      sx={{
                        color: "white",
                        backgroundColor: "transparent",
                        display: "flex",
                        alignItems: "center",
                        fontSize: "13px",
                        padding: "8px 12px",
                        minWidth: "auto",
                        borderBottom:
                          activeSection === page
                            ? "2px solid #f7f7f8"
                            : "2px solid transparent",
                        transition: "all 0.3s ease",
                        fontFamily:
                          "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif",
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                        "&:hover": {
                          borderBottom: "2px solid #a1a1aa",
                          transform: "translateY(-1px)",
                          color: "#d1d5db",
                        },
                      }}
                    >
                      <TextScramble>{page}</TextScramble>
                    </ScrambleScope>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Desktop Navigation */}
            <Box
              sx={{
                flexGrow: 1,
                justifyContent: "center",
                display: "none",
                "@media (min-width: 1200px)": {
                  display: "flex",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: "20px",
                  justifyContent: "center",
                  maxWidth: "100%",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {pages.map((page) => (
                  <ScrambleScope
                    component={Button}
                    key={page}
                    onClick={() => scrollToSection(page)}
                    sx={{
                      color: "white",
                      backgroundColor: "transparent",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "15px",
                      padding: "12px 16px",
                      minWidth: "auto",
                      borderBottom:
                        activeSection === page
                          ? "2px solid #f7f7f8"
                          : "2px solid transparent",
                      transition: "all 0.3s ease",
                      fontFamily:
                        "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      "&:hover": {
                        borderBottom: "2px solid #a1a1aa",
                        transform: "translateY(-1px)",
                        color: "#d1d5db",
                      },
                    }}
                  >
                    <TextScramble>{page}</TextScramble>
                  </ScrambleScope>
                ))}
              </Box>
            </Box>
          </Toolbar>
        </Container>
      </NavigationBar>

      {/* Hero Section */}
      <HeroSection ref={homeRef}>
        <HeroContentWrapper>
          <HeroContent>
            <HeroPhoto>
              <HeroImage src={profile} alt="Chenyu Zhang" />
            </HeroPhoto>
            <HeroText>
              <HeroTitle className="jitter">
                <TextScramble>Chenyu Zhang</TextScramble>
                <span className="dot">.</span>
              </HeroTitle>
              <KeywordList className="jitter">
                <TextScramble>
                  I am working to ensure that as machines get smarter, humans do
                  too.
                </TextScramble>
              </KeywordList>
              <CollaborationText>
                Always happy to connect. If you'd like to collaborate or just
                chat, reach out!
              </CollaborationText>

              <ContactEmailOptions>
                <ContactEmailOption>
                  <span>Personal:</span>
                  <ContactEmailLink
                    onClick={() =>
                      handleEmailClick(["contact", "chenyuzhang.com"])
                    }
                    title="contact@chenyuzhang.com"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </ContactEmailLink>
                </ContactEmailOption>
                <ContactEmailOption>
                  <span>Academic:</span>
                  <ContactEmailLink
                    onClick={() =>
                      handleEmailClick(["chenyu_zhang", "alumni.harvard.edu"])
                    }
                    title="chenyu_zhang@alumni.harvard.edu"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </ContactEmailLink>
                </ContactEmailOption>
                <ContactEmailOption>
                  <span>Links:</span>
                  <ContactIcon
                    href="https://scholar.google.com/citations?user=U9_JhR8AAAAJ"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Google Scholar"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                      <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                    </svg>
                  </ContactIcon>
                  <ContactIcon
                    href="https://github.com/charliechenyuzhang"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="GitHub"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
                    </svg>
                  </ContactIcon>
                  <ContactIcon
                    href="https://x.com/chenyuzhang"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="X"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </ContactIcon>
                  <ContactIcon
                    href="https://www.linkedin.com/in/thechenyuzhang/"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="LinkedIn"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"></path>
                      <rect x="2" y="9" width="4" height="12"></rect>
                      <circle cx="4" cy="4" r="2"></circle>
                    </svg>
                  </ContactIcon>
                </ContactEmailOption>
              </ContactEmailOptions>
            </HeroText>
          </HeroContent>
        </HeroContentWrapper>
        <ScrollIndicator>Scroll to explore</ScrollIndicator>
      </HeroSection>

      {/* BIO Section */}
      <ContentSection ref={bioRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Bio</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <ExperienceItem>
                <ExperienceContent>
                  <ExperienceTitle>
                    Cross-disciplinary technologist, educator, and entrepreneur
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Chenyu Zhang is a cross-disciplinary technologist, educator,
                    and entrepreneur redefining how people learn in the age of
                    AI. With over a decade of software engineering experience,
                    he has delivered large-scale systems and built platforms
                    serving 51 Canadian universities at Manulife, and scaled
                    AI-driven legal research at YC-backed ROSS Intelligence. His
                    research at MIT Media Lab, Stanford HAI, and the Harvard
                    Berkman Klein Center advances affective computing, large
                    language models, and multi-agent collaboration, with
                    publications at NeurIPS, AAAI, AERA, ACII, CSSSA, and
                    SIGCSE. A passionate educator, he has taught 1,000+ learners
                    worldwide in AI and computer science, mentored
                    underrepresented students through AI4ALL, co-founded the
                    Second-Year Learning Community at the University of Toronto
                    (featured by U of T News), and co-organized the
                    Decentralized AI Summit with over 1,000 participants at MIT.
                    Chenyu holds a Master's in Learning Design, Innovation, and
                    Technology from Harvard University and a Bachelor's in
                    Computer Science from the University of Toronto, and his
                    vision is clear: to give every learner access to a
                    world-class AI-powered tutor and the confidence to thrive.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* EDUCATION Section */}
      <ContentSection ref={educationRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Education</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/harvard.png")}
                    alt="Harvard University"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2024–2025
                    <ExperienceLocation>Cambridge, MA</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Master's in Education, Harvard University
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Learning Design, Innovation, and Technology. Core Courses:
                    Affective Computing, Distributed Computing, Multimodal Large
                    Language Models.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              {/* <PortfolioImageContainer>
                <div>
                  <PortfolioImage
                    src={require("../images/portfolio-photos/harvard_graduation.jpg")}
                    alt="Harvard Graduation"
                  />
                  <PortfolioImageCaption>
                    Harvard Commencement, 2025.
                  </PortfolioImageCaption>
                </div>
              </PortfolioImageContainer> */}

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/utoronto.png")}
                    alt="University of Toronto"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2015–2019
                    <ExperienceLocation>Toronto, ON</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Honours Bachelor of Science, University of Toronto
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Computer Science, Mathematics & Statistics. Specialist
                    Program with focus on Artificial Intelligence. Graduated
                    with High Distinction.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* PUBLICATIONS Section */}
      <ContentSection ref={publicationsRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Publications</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <PublicationGroup>
                <GroupTitle>Published Papers</GroupTitle>

                <PublicationItem>
                  <ConferenceColumn>
                    <ConferenceBadge>CHI'26</ConferenceBadge>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Minsol Kim, David Lafond, Chloe Shim, Michelle Han,
                      Mohanad Kandil, <strong>Chenyu Zhang</strong>, Theo
                      Kitsberg, Daniel Low, Chelsea Boccagno, Paul Pu Liang, and
                      Pattie Maes. (2026). Breaking the Negative Cycle: A
                      Self-Reflection System for Adaptive Change. Submitted to
                      the ACM CHI Conference on Human Factors in Computing
                      Systems (CHI 2026).
                    </ExperienceDescription>
                    <div style={{ marginTop: "0.5rem" }}>
                      <SectionLink
                        href="https://dl.acm.org/doi/10.1145/3772318.3791615"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Paper
                        <OpenInNewIcon />
                      </SectionLink>
                    </div>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <ConferenceBadge>AAAI'26</ConferenceBadge>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      <strong>Chenyu Zhang</strong> and Xiaohang Luo. (2026).
                      Scaling Equitable Reflection Assessment in Education via
                      Large Language Models and Role-Based Feedback Agents.
                      Accepted to AAAI 2026 Artificial Intelligence for Social
                      Impact (AISI) Track.
                    </ExperienceDescription>
                    <div
                      style={{
                        marginTop: "0.5rem",
                        display: "flex",
                        gap: "1rem",
                      }}
                    >
                      <SectionLink
                        href="https://arxiv.org/abs/2511.11772"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Paper
                        <OpenInNewIcon />
                      </SectionLink>
                      <SectionLink
                        href="https://drive.google.com/file/d/1z74BL13ff8V4t_-KjAsOg7G8h1P76-EN/view?usp=sharing"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Poster
                        <OpenInNewIcon />
                      </SectionLink>
                    </div>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <ConferenceBadge>AERA'26</ConferenceBadge>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      <strong>Chenyu Zhang</strong> and Xiaohang Luo. (2025).
                      Role-Based Generative AI for Equitable Formative Feedback:
                      Mixed-Methods Evidence from a Web-Based Course. Accepted
                      to AERA 2026.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <ConferenceBadge>ACII'25</ConferenceBadge>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      <strong>Chenyu Zhang</strong>, Sharifa Alghowinem, and
                      Cynthia Breazeal. (2025). Ensembling Large Language Models
                      to Characterise Affective Dynamics in Student–AI Tutor
                      Dialogues. Proceedings of the 11th International
                      Conference on Affective Computing and Intelligent
                      Interaction (ACII 2025), Late-Breaking Results Track.
                    </ExperienceDescription>
                    <div
                      style={{
                        marginTop: "0.5rem",
                        display: "flex",
                        gap: "1rem",
                      }}
                    >
                      <SectionLink
                        href="https://arxiv.org/pdf/2510.13862"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Paper
                        <OpenInNewIcon />
                      </SectionLink>
                      <SectionLink
                        href="https://drive.google.com/file/d/10bc6IgrxIBIcVy_ZSaDvTf5oNz6YVClW/view?usp=sharing"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Slides
                        <OpenInNewIcon />
                      </SectionLink>
                    </div>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <ConferenceBadge>NeurIPS'25 MAR</ConferenceBadge>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      <strong>Chenyu Zhang</strong>, Minsol Kim, Shohreh
                      Ghorbani, Jingyao Wu, Patricia Maes, and Paul Pu Liang.
                      (2025). When One Modality Sabotages the Others: A
                      Diagnostic Lens on Multimodal Reasoning. Proceedings of
                      the Multimodal Algorithmic Reasoning (MAR 2025) Workshop
                      at the Conference on Neural Information Processing Systems
                      (NeurIPS 2025).
                    </ExperienceDescription>
                    <div
                      style={{
                        marginTop: "0.5rem",
                        display: "flex",
                        gap: "1rem",
                      }}
                    >
                      <SectionLink
                        href="https://arxiv.org/pdf/2511.02794"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Paper
                        <OpenInNewIcon />
                      </SectionLink>
                      <SectionLink
                        href="https://drive.google.com/file/d/1ClgcOD46jaOQvvN3V8lD-mHiB68PFD6C/view?usp=sharing"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Poster
                        <OpenInNewIcon />
                      </SectionLink>
                    </div>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <ConferenceBadge>NeurIPS'25 LLM-EVAL</ConferenceBadge>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Shohreh Ghorbani, <strong>Chenyu Zhang</strong>, Minsol
                      Kim, and Jingyao Wu. (2025). Beyond Accuracy: A Diagnostic
                      Protocol for Fairly Evaluating Multimodal Reasoning.
                      Poster presented at the LLM Evaluation (LLM-Eval 2025)
                      Workshop, Conference on Neural Information Processing
                      Systems (NeurIPS 2025).
                    </ExperienceDescription>
                    <div
                      style={{
                        marginTop: "0.5rem",
                        display: "flex",
                        gap: "1rem",
                      }}
                    >
                      <SectionLink
                        href="https://openreview.net/pdf?id=2S7VgHrx20"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Paper
                        <OpenInNewIcon />
                      </SectionLink>
                    </div>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <ConferenceBadge>CSSSA'25</ConferenceBadge>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      <strong>Chenyu Zhang</strong> and Xingyu Li. (2025).
                      Building Research-Ready Textual Datasets at Scale: An LLM
                      Case Study on Mindfulness Prompts. Proceedings of the
                      Computational Social Science Society of the Americas
                      Conference (CSSSA 2025), Poster Track.
                    </ExperienceDescription>
                    <div
                      style={{
                        marginTop: "0.5rem",
                        display: "flex",
                        gap: "1rem",
                      }}
                    >
                      <SectionLink
                        href="https://www.youtube.com/shorts/5lljspUH8Bs"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Talk
                        <OpenInNewIcon />
                      </SectionLink>
                      <SectionLink
                        href="https://drive.google.com/file/d/1mAlcDLN7QPvi7jlBAWC4K1c1x4Pp7pb2/view?usp=sharing"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Poster
                        <OpenInNewIcon />
                      </SectionLink>
                    </div>
                  </PublicationContent>
                </PublicationItem>

                {/* CSSSA Conference photos - hidden for now */}
                {/* <PhotoAlbumContainer>
                  <PhotoAlbum
                    photos={csssaPhotos}
                    layout="masonry"
                    columns={(containerWidth) => {
                      if (containerWidth < 600) return 1;
                      if (containerWidth < 900) return 2;
                      return 4;
                    }}
                    onClick={({ index }) => {
                      setLightboxIndex(index);
                      setLightboxOpen(true);
                    }}
                    spacing={16}
                  />
                  <PortfolioImageCaption style={{ marginTop: "1rem" }}>
                    CSSSA Conference, 2025.
                  </PortfolioImageCaption>
                  <Lightbox
                    open={lightboxOpen}
                    close={() => setLightboxOpen(false)}
                    index={lightboxIndex}
                    slides={csssaPhotos}
                  />
                </PhotoAlbumContainer> */}

                <PublicationItem>
                  <ConferenceColumn>
                    <ConferenceBadge>SIGCSE'21</ConferenceBadge>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Jacqueline Smith, Jennifer Campbell, Jennifer Evans, and{" "}
                      <strong>Chenyu Zhang</strong>. (2021). Building Community
                      in a Competitive Undergraduate Program. In Proceedings of
                      the 52nd ACM Technical Symposium on Computer Science
                      Education (SIGCSE '21).
                    </ExperienceDescription>
                    <div style={{ marginTop: "0.5rem" }}>
                      <SectionLink
                        href="https://doi.org/10.1145/3408877.3432518"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Paper
                        <OpenInNewIcon />
                      </SectionLink>
                    </div>
                  </PublicationContent>
                </PublicationItem>
              </PublicationGroup>
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* REVIEWER Section */}
      <ContentSection ref={reviewerRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Paper Reviewer</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <PublicationGroup>
                <GroupTitle>Conferences</GroupTitle>

                <PublicationItem>
                  <ConferenceColumn>
                    <SectionLink
                      href="https://aaai.org/conference/aaai/aaai-27/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ConferenceBadge>AAAI'27</ConferenceBadge>
                      <OpenInNewIcon />
                    </SectionLink>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Reviewer, <strong>
                        The Forty-First AAAI Conference on Artificial Intelligence
                      </strong>, 2027.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <SectionLink
                      href="https://iui.acm.org/2027"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ConferenceBadge>IUI'27</ConferenceBadge>
                      <OpenInNewIcon />
                    </SectionLink>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Program Committee (PC) Member, <strong>ACM International Conference on Intelligent User Interfaces</strong>, 2027.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <SectionLink
                      href="https://icml.cc/2026"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ConferenceBadge>ICML'26</ConferenceBadge>
                      <OpenInNewIcon />
                    </SectionLink>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Reviewer, <strong>Forty-Third International Conference on Machine Learning</strong>. Recognized as a <strong style={{ color: "#d4af37" }}>Gold Reviewer</strong>, placing among the top 25% of reviewers.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <SectionLink
                      href="https://neurips.cc/Conferences/2026"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ConferenceBadge>NeurIPS'26</ConferenceBadge>
                      <OpenInNewIcon />
                    </SectionLink>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Reviewer, <strong>Neural Information Processing Systems</strong>, 2026.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <SectionLink
                      href="https://aaai.org/aaai-conference/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ConferenceBadge>AAAI'26</ConferenceBadge>
                      <OpenInNewIcon />
                    </SectionLink>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Reviewer, <strong>
                        Artificial Intelligence for Social Impact Track
                      </strong>.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <SectionLink
                      href="https://chi2026.acm.org/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ConferenceBadge>CHI'26</ConferenceBadge>
                      <OpenInNewIcon />
                    </SectionLink>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Reviewer, <strong>Papers Track</strong>, 2025–2026.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <SectionLink
                      href="https://iui.acm.org/2026"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ConferenceBadge>IUI'26</ConferenceBadge>
                      <OpenInNewIcon />
                    </SectionLink>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Program Committee Member & Reviewer, <strong>Intelligent User Interfaces</strong>, 2025–2026.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <SectionLink
                      href="https://humanrobotinteraction.org/2026"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ConferenceBadge>HRI'26</ConferenceBadge>
                      <OpenInNewIcon />
                    </SectionLink>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Reviewer, <strong>Human-Robot Interaction</strong>.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <SectionLink
                      href="https://ecis2026.it/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ConferenceBadge>ECIS'26</ConferenceBadge>
                      <OpenInNewIcon />
                    </SectionLink>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Reviewer, European Conference on Information Systems (ECIS), 2026.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>

              </PublicationGroup>

              <PublicationGroup>
                <GroupTitle>Workshops</GroupTitle>

                <PublicationItem>
                  <ConferenceColumn>
                    <SectionLink
                      href="https://sites.google.com/view/llm-eval-workshop/call-for-papers?authuser=0"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ConferenceBadge>NeurIPS'25</ConferenceBadge>
                      <OpenInNewIcon />
                    </SectionLink>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      Reviewer, <strong>
                        Evaluating the Evolving LLM Lifecycle: Benchmarks,
                        Emergent Abilities, and Scaling Workshop
                      </strong>.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>
              </PublicationGroup>
              <PublicationGroup>
                <GroupTitle>Competition</GroupTitle>

                <PublicationItem>
                  <ConferenceColumn>
                    <ConferenceBadge>MIT $100K</ConferenceBadge>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      <strong>Judge</strong>, MIT $100K Entrepreneurship
                      Competition (Accelerate 2026 Online Preliminaries), 2026.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <ConferenceBadge>MIT $100K</ConferenceBadge>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      <strong>Judge</strong>, MIT $100K Entrepreneurship
                      Competition (Launch), 2025. Launch is the final and most
                      competitive track of the year, with $100K on the line.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>

                <PublicationItem>
                  <ConferenceColumn>
                    <ConferenceBadge>MIT $100K</ConferenceBadge>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      <strong>Judge</strong>, MIT $100K Entrepreneurship
                      Competition, 2025.
                    </ExperienceDescription>
                  </PublicationContent>
                </PublicationItem>
              </PublicationGroup>
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* EXPERIENCE Section */}
      <ContentSection ref={experienceRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Work Experience</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/manulife.jpeg")}
                    alt="Manulife"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2019–2024
                    <ExperienceLocation>Toronto, ON</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Full Stack Software Engineer, Manulife
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Consistently ranked exceptional in performance, built B2C
                    assets for millions of users, including Costco, CAA, and 51
                    Canadian universities. Championed web accessibility for
                    visually impaired users and delivered customized digital
                    experiences for over 2,000 agents, showcasing a commitment
                    to inclusive and universal design principles.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/ross.jpeg")}
                    alt="ROSS Intelligence"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2019
                    <ExperienceLocation>Toronto, ON</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Software Engineer, ROSS Intelligence
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Built reusable components and developed features including
                    the decision reader, top navigation bar, omnibar, document
                    analyzer etc. and closed 36+ various bug tickets using
                    TypeScript, React, Redux, Saga, Python3 and Groovy. Built a
                    Statutes and Regulation (SnR) hyperlinking microservice that
                    extracts and highlights SnR citations in 5 million cases
                    using Python3, Docker, Kubernetes, Amazon S3 and AWS Batch.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              {/* <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/stealth.png")}
                    alt="Big Solve Robotics"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2016
                    <ExperienceLocation>Toronto, ON</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Software Engineering Intern, Big Solve Robotics
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Solved problems of the previous software that can only
                    control one motor at a time by implementing a solution that
                    controls 7 motors simultaneously and displayed a User
                    Interface for control manuals with real-time data plots
                    using Python and C. Assembled robots and connected motors,
                    drivers and power converters. Showcased 2 robot prototypes
                    at the Toronto Maker Festival to 511+ participants.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem> */}
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* RESEARCH Section */}
      <ContentSection ref={researchRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Research</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/bkc-logo.jpeg")}
                    alt="Harvard University"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2024–2026
                    <ExperienceLocation>Cambridge, MA</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Research Assistant, Berkman Klein Center, Harvard University
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Developing methods for steering agentic conversational AI
                    behavior, formalizing three interpretable control
                    dimensions: rigidity, independence, and goal persistence,
                    motivated by goals in safety, and controllability
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/mitmedialab.jpg")}
                    alt="MIT Media Lab"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2024–2025
                    <ExperienceLocation>Cambridge, MA</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Independent Researcher, MIT Media Lab
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Personal Robots Group. Led and authored ACII 2025
                    publication on large language models (LLMs) and affective
                    computing.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/stanford.jpeg")}
                    alt="Stanford HAI"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2025
                    <ExperienceLocation>Remote</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Research Assistant, Stanford HAI
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Cohen & Sahami Lab, mentored by Xingyu Li. Led and published
                    a first-author paper at CSSSA 2025, conducting original
                    research in large language models (LLMs) and computational
                    social science.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* ENTREPRENEURSHIP Section */}
      <ContentSection ref={entrepreneurshipRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Entrepreneurship</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/stealth.png")}
                    alt="GlowingStar Inc"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2024–Present
                    <ExperienceLocation>Cambridge, MA</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>Founder, Stealth Startup</ExperienceTitle>
                  <ExperienceDescription>
                    Building AI-native workflow systems.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/stealth.png")}
                    alt="BitByBit Life"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2019
                    <ExperienceLocation>Toronto, ON</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>Founder, BitByBit Life</ExperienceTitle>
                  <ExperienceDescription>
                    Built a listener and empathy bot for people experiencing
                    stress and stress-induced illnesses. Later pivoted to a
                    marketplace to connect personal trainers, nutritionists and
                    clients.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/stealth.png")}
                    alt="UpReview"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2017–2018
                    <ExperienceLocation>Lisbon, Portugal</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>Founder, UpReview</ExperienceTitle>
                  <ExperienceDescription>
                    Built a startup at the European Innovation Academy with full
                    scholarship. Organized a team of 5 people working on a
                    solution to improve the efficiency of queueing systems in
                    pharmacies in the UK. Built a Chatbot prototype by iterating
                    over 11 prototypes using Proto.io and Chatfuel, and
                    demonstrated the project to 311 audiences, 3 investors.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/stealth.png")}
                    alt="Udiply"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2016–2017
                    <ExperienceLocation>Toronto, ON</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>Founder, Udiply</ExperienceTitle>
                  <ExperienceDescription>
                    Led a team of 6 people working on 3 prototypes that generate
                    tailored feedbacks on users' resumes based on different jobs
                    they apply. Obtained 68 potential customers, produced 3
                    advertising videos, 1 business plan by interviewing 122
                    potential users. Selected to participate in Toronto Founder
                    Institute, UofT Entrepreneurship Hatchery, and Y Combinator
                    Online Startup School.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* TEACHING Section */}
      <ContentSection ref={teachingRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>University Teaching</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/mitmedialab.jpg")}
                    alt="MIT Media Lab"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2025
                    <ExperienceLocation>Cambridge, MA</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Teaching Assistant, MIT Media Lab (MAS.S63 Affective
                    Computing and Multimodal Interaction)
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Supporting Professor Rosalind Picard and Professor Paul
                    Liang in teaching the foundational course on Affective
                    Computing, a field birthed at the MIT Media Lab. Assisting
                    with course materials, student projects, and discussions on
                    topics including wearable emotion recognition, multimodal
                    human-computer interaction, emotional intelligence in
                    robotics, and ethical considerations in affective computing.
                    The course covers the intersection of psychology, computer
                    science, and human-computer interaction.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/mitmedialab.jpg")}
                    alt="MIT Media Lab"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2025
                    <ExperienceLocation>Cambridge, MA</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Teaching Assistant, MIT Media Lab (MAS.664 AI Venture
                    Studio)
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Evaluated ~200 technical assignments. Organized the
                    Decentralized AI Summit with over 1,000 participants.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/chengdu.png")}
                    alt="Chengdu University"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2025
                    <ExperienceLocation>Remote</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Instructor, Chengdu University
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Designed and delivered a 16-hour hands-on course on Prompt
                    Engineering and LLM Agent Design to 30 undergraduate
                    students, covering cutting-edge topics such as
                    zero-shot/few-shot prompting, Retrieval-Augmented Generation
                    (RAG), Chain-of-Thought reasoning, agent workflows, and
                    multimodal prompting.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/stanford.png")}
                    alt="Stanford University"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2024–2025
                    <ExperienceLocation>Remote</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Section Leader, Stanford University: Code In Place
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Facilitated weekly interactive sessions for a diverse group
                    of international students with varying backgrounds, aiming
                    to introduce the fundamentals of Python programming.
                    Emphasized hands-on learning through projects and
                    assignments that inspire creativity and practical
                    application. Provided guidance, support, and feedback to
                    students to enhance their understanding of programming
                    concepts and problem-solving techniques.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/utoronto.png")}
                    alt="University of Toronto"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2019
                    <ExperienceLocation>Toronto, ON</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Teaching Assistant, University of Toronto
                  </ExperienceTitle>
                  <ExperienceDescription>
                    TAed a cross-listed graduate/undergraduate course
                    CSC401/2511, Natural Language Computing, under the
                    supervision of Professor Frank Rudzicz and Chloé Pou-Prom.
                    Designed auto-tester for Assignment 2 and 3. Held tutorials
                    covering topics in assignments.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/hsylc.png")}
                    alt="Harvard University"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2017
                    <ExperienceLocation>Remote</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Teaching Fellow, Harvard Summit for Young Leaders in China
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Conducted a meeting with a group of 29 Chinese high school
                    students every day for 9 days to build up their confidence
                    and strengthen the bond between each other. Co-hosted
                    seminars for around 100 Chinese students in three sessions,
                    and attended four office hours and answered more than 41
                    questions raised by students of the topic of Artificial
                    Intelligence and its impact.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>
            </ContentColumn>
          </div>
          <div
            style={{ maxWidth: "1100px", margin: "0 auto", marginTop: "4rem" }}
          >
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Private Tutoring</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/stealth.png")}
                    alt="Computer Science Tutor"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2022–Present
                    <ExperienceLocation>Remote</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>Computer Science Tutor</ExperienceTitle>
                  <ExperienceDescription>
                    Delivered 195 sessions on web development, instructing over
                    1,760+ master's students and Ph.D. candidates. Guided 30+
                    teams through a month-long initiative to conceptualize,
                    design, and launch software projects from scratch. Designed
                    and taught "Next AI," an AI curriculum leveraging langchain
                    and GPT-4 API for PDF Q&A.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* SPEAKING Section */}
      <ContentSection ref={speakingRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Public Speaking & Presentations</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/MLOPs-Community.png")}
                    alt="MLOps Community"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    November 18, 2025
                    <ExperienceLocation>Virtual</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Agents in Production Virtual Conference
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Presented "When Agents Learn to Feel: Multi-Modal Affective
                    Computing in Production" at the Agents in Production Virtual
                    Conference on November 18, hosted by MLOps community and
                    Prosus Ventures. Shared learnings from deploying
                    emotion-aware AI tutors in production, including how
                    multi-modal signals (voice tone, facial micro-expressions,
                    text patterns, and response latency) combine to understand
                    emotional state. Covered system design choices, multi-agent
                    coordination, modality conflicts, and privacy safeguards for
                    education. The reception demonstrated that affective
                    intelligence is becoming critical to the next wave of AI
                    agents.
                  </ExperienceDescription>
                  <div style={{ marginTop: "0.5rem" }}>
                    <SectionLink
                      href="https://docs.google.com/presentation/d/1l9wjWaC4RB1Jj7PFN5bUItoH8TMWGwFb-EIwS2hjYmY/edit?usp=sharing"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Slides
                      <OpenInNewIcon />
                    </SectionLink>
                  </div>
                </ExperienceContent>
              </ExperienceItem>

              {/* <PortfolioImageContainer>
                <div>
                  <PortfolioImage
                    src={require("../images/portfolio-photos/mlops.jpeg")}
                    alt="Agents in Production Virtual Conference"
                  />
                  <PortfolioImageCaption>
                    Agents in Production Virtual Conference, November 18, 2025.
                  </PortfolioImageCaption>
                </div>
              </PortfolioImageContainer> */}

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/mitmedialab.jpg")}
                    alt="MIT AI Venture Studio"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2025
                    <ExperienceLocation>Cambridge, MA</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Pitch at MIT AI Venture Studio
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Presented SPARK Journaling, an AI-powered companion designed
                    to promote self-compassion. Delivered a compelling pitch
                    showcasing the innovative intersection of AI technology and
                    mental health support.
                  </ExperienceDescription>
                  <div style={{ marginTop: "0.5rem" }}>
                    <SectionLink
                      href="https://www.youtube.com/watch?v=8uddXOx3LLU"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Pitch
                      <OpenInNewIcon />
                    </SectionLink>
                  </div>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/harvard.png")}
                    alt="Harvard Kennedy School"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2024
                    <ExperienceLocation>Cambridge, MA</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Public Narrative at Harvard Kennedy School
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Shared a deeply personal story about overcoming high school
                    bullying, demonstrating vulnerability and resilience. This
                    narrative presentation highlighted the power of storytelling
                    in leadership and personal growth.
                  </ExperienceDescription>
                  <div style={{ marginTop: "0.5rem" }}>
                    <SectionLink
                      href="https://www.youtube.com/watch?v=GOc2CVToSbU"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Presentation
                      <OpenInNewIcon />
                    </SectionLink>
                  </div>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/utoronto.png")}
                    alt="Leading Together Conference"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2017
                    <ExperienceLocation>Toronto, ON</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Conference Speaker, Leading Together Conference
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Leading Together: A Student Leadership Conference is an
                    opportunity to connect with student leaders across roles,
                    areas of study, and experiences at the University of
                    Toronto.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/portugal.png")}
                    alt="Philippine Embassy in Portugal"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2017
                    <ExperienceLocation>Lisbon, Portugal</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Guest Speaker, Philippine Embassy in Portugal
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Invited as an inspirational speaker at the Philippine
                    Embassy in Portugal.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* VOLUNTEERING Section */}
      <ContentSection ref={volunteeringRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Volunteering</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/ai4all.png")}
                    alt="AI4ALL"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2025
                    <ExperienceLocation>Remote</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Mentor - Resume and LinkedIn Review, AI4ALL
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Guided and mentored students during two interactive
                    40-minute sessions, providing tailored advice on improving
                    their resumes, LinkedIn profiles, and personal websites.
                    Shared insights from professional journey in AI, software
                    engineering, and entrepreneurship to inspire and empower
                    students exploring careers in technology.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/ai4all.png")}
                    alt="AI4ALL"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2024
                    <ExperienceLocation>Remote</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Workshop Presenter - Technical Interview Practice Questions,
                    AI4ALL
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Developed and led a 90-minute workshop focused on technical
                    interview practice questions.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/utoronto.png")}
                    alt="University of Toronto"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2017
                    <ExperienceLocation>Toronto, ON</ExperienceLocation>
                  </ExperienceSubtitle>

                  <ExperienceTitle>
                    Next Steps Conference Student Leader, University of Toronto
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Help to increase comfort with networking, by facilitating
                    the Homeroom activity focused on networking and helping to
                    create networking connections at the Networking Reception.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/utoronto.png")}
                    alt="University of Toronto"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2017
                    <ExperienceLocation>Toronto, ON</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Facilitator, PRE U of T 2017
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Hosted a panel discussion with two U of T professors
                    answering questions and offering tips in terms of Computer
                    Science with around 300 incoming first-year students as
                    participants.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/utoronto.png")}
                    alt="University of Toronto"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2017
                    <ExperienceLocation>Toronto, ON</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Thrive Orientation Leader, University of Toronto -
                    University College
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Acted as an orientation leader leading a group of 30
                    students doing various activities during two-day
                    orientation. Delivered a closing speech to the Thrive
                    orientation.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/cutc.jpeg")}
                    alt="CUTC Foundation"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2017–2018
                    <ExperienceLocation>Toronto, ON</ExperienceLocation>
                  </ExperienceSubtitle>
                  <ExperienceTitle>
                    Sponsorship Lead, CUTC Foundation
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Canada Undergraduate Technology Conference is Canada's
                    longest running tech conference, managed $60k in sponsorship
                    funds. Worked with a team of 3 people and reached
                    sponsorship agreements w/ 15+ companies including Google,
                    Microsoft, Deloitte, TD, Sunlife, 8VC, Vidyard etc.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* COMMUNITY Section */}
      <ContentSection ref={communityRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Community Leadership</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/microsoft.jpeg")}
                    alt="Microsoft"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>2018–2019</ExperienceSubtitle>
                  <ExperienceTitle>
                    Student Partner, Microsoft Student Partner Program
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Judged and selected the winning team for the Azure Champ
                    Prize at the UofT Hack 2019. Delivered 4 lectures at Coding
                    Hive in 2018 and 2019, educating over 120 high school
                    students about ML. Toronto, ON.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/utoronto.png")}
                    alt="University of Toronto"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>2017–2019</ExperienceSubtitle>
                  <ExperienceTitle>
                    Co-Founder and Peer Mentor, Second Year Learning Community
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Spearheaded the development and implementation of the SLC
                    program aimed at fostering a sense of community among
                    second-year Computer Science students. Collaborated with
                    Professors Jacqueline Smith and Jennifer Campbell to
                    facilitate bi-weekly meetups for 30+ students. Toronto, ON.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* CERTIFICATIONS Section */}
      <ContentSection ref={certificationsRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Certifications</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <ExperienceItem>
                <ExperienceContent>
                  <ExperienceSubtitle>Coaching</ExperienceSubtitle>
                  <ExperienceTitle>
                    Designing Your Life Certified Coach, The Designing Your Life
                    Group
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Certified coach trained in the Designing Your Life
                    methodology from Stanford University's d.school. Specialized
                    in helping individuals navigate life transitions with
                    clarity, purpose, and agency.
                  </ExperienceDescription>
                  <div style={{ marginTop: "0.5rem" }}>
                    <SectionLink
                      href="https://designingyour.life/find-a-coach/coaches/chenyu-zhang/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Profile
                      <OpenInNewIcon />
                    </SectionLink>
                  </div>
                </ExperienceContent>
              </ExperienceItem>
              {/* <PortfolioImageGallery>
                <div>
                  <PortfolioImage
                    src={require("../images/coach-badge@4x.png")}
                    alt="Certified Designing Your Life Coach badge"
                  />
                </div>
                <div>
                  <PortfolioImage
                    src={require("../images/portfolio-photos/design-your-life.png")}
                    alt="Designing Your Life Profile Website"
                  />
                  <PortfolioImageCaption>
                    Designing Your Life Profile Website.
                  </PortfolioImageCaption>
                </div>
              </PortfolioImageGallery> */}

              <ExperienceItem>
                <ExperienceContent>
                  <ExperienceSubtitle>Training</ExperienceSubtitle>
                  <ExperienceTitle>
                    Co-Active Coach Training, Co-Active Training Institute
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Professional coach training program focused on developing
                    coaching competencies and skills for supporting personal and
                    professional growth.
                  </ExperienceDescription>
                  <div style={{ marginTop: "0.5rem" }}>
                    <SectionLink
                      href="https://drive.google.com/file/d/15T46wI-DtCaKDFuiyXVNpMHu9v_rI1Yv/view?usp=sharing"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Certificate
                      <OpenInNewIcon />
                    </SectionLink>
                  </div>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <ExperienceContent>
                  <ExperienceSubtitle>Research Ethics</ExperienceSubtitle>
                  <ExperienceTitle>
                    Social & Behavioral Research Investigators, CITI Program
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Certified in responsible conduct of research involving human
                    subjects in social and behavioral sciences.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <ExperienceContent>
                  <ExperienceSubtitle>Research Ethics</ExperienceSubtitle>
                  <ExperienceTitle>
                    Biomedical Research Investigators, CITI Program
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Certified in responsible conduct of research involving human
                    subjects in biomedical research.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* AWARDS Section */}
      <ContentSection ref={awardsRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>Awards & Recognition</TextScramble>
            </SectionHeading>
            <ContentColumn>
              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/utoronto.png")}
                    alt="University of Toronto"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>2019</ExperienceSubtitle>
                  <ExperienceTitle>UC Merit Award</ExperienceTitle>
                  <ExperienceDescription>
                    Awarded for outstanding academic achievement and
                    contributions to the university community.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/utoronto.png")}
                    alt="University of Toronto"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>
                    2016, 2017, 2018, 2019
                  </ExperienceSubtitle>
                  <ExperienceTitle>Dean's List Scholar</ExperienceTitle>
                  <ExperienceDescription>
                    Consistently recognized for academic excellence at the
                    University of Toronto.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/forbes.jpeg")}
                    alt="Forbes"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>2017, 2018</ExperienceSubtitle>
                  <ExperienceTitle>Forbes Under 30 Scholars</ExperienceTitle>
                  <ExperienceDescription>
                    Recognized as one of the top young scholars in the field.
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>

              <ExperienceItem>
                <CompanyLogo>
                  <img
                    src={require("../images/company-logos/microsoft.jpeg")}
                    alt="Microsoft"
                  />
                </CompanyLogo>
                <ExperienceContent>
                  <ExperienceSubtitle>2016</ExperienceSubtitle>
                  <ExperienceTitle>
                    Bronze Medal (3rd of 311), Microsoft College Code
                    Competition
                  </ExperienceTitle>
                  <ExperienceDescription>
                    Achieved third place out of 311 participants in the
                    Microsoft College Code Competition (MSFT3C).
                  </ExperienceDescription>
                </ExperienceContent>
              </ExperienceItem>
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/*
        MUSIC, DANCE, and SINGING sections hidden for now.
        Restore the pages and sectionRefs entries above when re-enabling.
      */}
      {false && (
        <>
          <ContentSection ref={musicRef}>
            <SectionContainer>
              <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
                <SectionHeading
                  style={{ marginBottom: "1.25rem", textAlign: "left" }}
                >
                  <TextScramble>Music</TextScramble>
                </SectionHeading>
                <ContentColumn>
                  <ExperienceContent>
                    <ExperienceDescription>
                      Music has been part of my life for as long as I can
                      remember. At just 18 months old, I'd stand outside a
                      practice room for hours, totally mesmerized. By five, I
                      was learning piano, spending summers perfecting scales and
                      arpeggios. Fast forward to 2021, I discovered EDM and
                      started experimenting with Logic Pro. I'm new to the genre,
                      but it's been a blast creating! You can check out a few of
                      my demos below. Hope you enjoy! 🎧
                    </ExperienceDescription>
                  </ExperienceContent>

                  <VideoGrid>
                    {[
                      {
                        name: "Exotic Dreams",
                        description:
                          "In the era of the pandemic, we couldn't travel far but why not let your mind wander to an exotic place where you can try a new dish, learn a foreign language and meet new friends from all over the world?",
                        link: "https://www.youtube.com/embed/DwhYip4cLcc?si=1WCOUb97E7Egpl_F",
                      },
                      {
                        name: "Deep Space",
                        description:
                          "When traveling in the deep space, what would you hear, what would you think, what would you see, what would you feel? This original music might have the answer for you.",
                        link: "https://www.youtube.com/embed/DSpVvy-jmZ0?si=zFXSVkvBH5xZvno1",
                      },
                      {
                        name: "Jasmine Flowers",
                        description:
                          "This song, Mo Li Hua or Jasmine Flowers, is and will remain one of my favourite Chinese folk songs of all time.",
                        link: "https://www.youtube.com/embed/oqZS5srPAB8?si=4ilrcxhy8L2SisbE",
                      },
                      {
                        name: "High Fashion",
                        description: "picture yourself in a high fashion show",
                        link: "https://www.youtube.com/embed/X7CDIZWsdVI?si=ifFHYNJBYCMK_4_n",
                      },
                      {
                        name: "SpaceX",
                        description:
                          "a music inspired by Space and its rocket launch",
                        link: "https://www.youtube.com/embed/zs-nqryR71I?si=NwgpuCXRl9Z8_DB5",
                      },
                    ].map((music, idx) => (
                      <VideoCard key={idx}>
                        <VideoTitle>{music.name}</VideoTitle>
                        <VideoDescription>{music.description}</VideoDescription>
                        <VideoIframe
                          src={music.link}
                          title={`${music.name} - YouTube video player`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </VideoCard>
                    ))}
                  </VideoGrid>
                </ContentColumn>
              </div>
            </SectionContainer>
          </ContentSection>

          <ContentSection ref={danceRef}>
            <SectionContainer>
              <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
                <SectionHeading
                  style={{ marginBottom: "1.25rem", textAlign: "left" }}
                >
                  <TextScramble>Dance</TextScramble>
                </SectionHeading>
                <ContentColumn>
                  <ExperienceContent>
                    <ExperienceDescription>
                      In 2021, I decided to step out of my comfort zone and see
                      if I could dance. I may not be a professional dancer yet,
                      but I've discovered the joy of movement and how much fun it
                      can be to keep improving!
                    </ExperienceDescription>
                  </ExperienceContent>

                  <VideoGrid>
                    {[
                      {
                        name: "Duet Dance Practice",
                        description:
                          "Location: Toronto, Canada\n Date: December 2021",
                        link: "https://www.youtube.com/embed/BQHPiXvFktM?si=p6dqpmQD-6wOC_Tr",
                      },
                      {
                        name: "Live Performance at DanceX Studio",
                        description:
                          "Location: Toronto, Canada\n Date: December 11, 2021",
                        link: "https://www.youtube.com/embed/EsUGIoVMJFo?si=u6s2_4ww8S2UOfZB",
                      },
                    ].map((video, idx) => (
                      <VideoCard key={idx}>
                        <VideoTitle>{video.name}</VideoTitle>
                        <VideoDescription>{video.description}</VideoDescription>
                        <VideoIframe
                          src={video.link}
                          title={`${video.name} - YouTube video player`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </VideoCard>
                    ))}
                  </VideoGrid>
                </ContentColumn>
              </div>
            </SectionContainer>
          </ContentSection>

          <ContentSection ref={singingRef}>
            <SectionContainer>
              <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
                <SectionHeading
                  style={{ marginBottom: "1.25rem", textAlign: "left" }}
                >
                  <TextScramble>Singing</TextScramble>
                </SectionHeading>
                <ContentColumn>
                  <ExperienceContent>
                    <ExperienceDescription>
                      Back in high school, I fell in love with Bel canto and
                      trained in the art of classical singing. Fast forward to
                      2022, I decided to mix things up and explore pop singing
                      with a fantastic teacher from Berklee College of Music.
                      Performing on stage is my happy place. There's nothing like
                      the rush of movement and connection. I hope you enjoy these
                      moments as much as I did creating them. 🎵
                    </ExperienceDescription>
                  </ExperienceContent>

                  <VideoGrid>
                    {[
                      {
                        name: "City of Stars | La La Land Cover",
                        description:
                          "Location: Toronto, Canada\n Date: December 10, 2022",
                        link: "https://chenyuzhang-com-assets.s3.us-east-1.amazonaws.com/singing/city-of-stars.MOV",
                        poster:
                          "https://chenyuzhang-com-assets.s3.us-east-1.amazonaws.com/singing/IMG_6556.jpg",
                      },
                      {
                        name: "Somewhere Over the Rainbow",
                        description:
                          "Location: Toronto, Canada\n Date: August 11, 2022",
                        link: "https://chenyuzhang-com-assets.s3.us-east-1.amazonaws.com/singing/somewhere-over-the-rainbow.mov",
                        poster:
                          "https://chenyuzhang-com-assets.s3.us-east-1.amazonaws.com/singing/IMG_6555.jpg",
                      },
                      {
                        name: "Have Yourself a Merry Little Christmas",
                        description:
                          "Location: Toronto, Canada\n Date: January 6, 2022",
                        link: "https://www.youtube.com/embed/4Yk2SSxiQSY?si=xM0nb0LPDFU9CxDN",
                      },
                    ].map((video, idx) => (
                      <VideoCard key={idx}>
                        <VideoTitle>{video.name}</VideoTitle>
                        <VideoDescription>{video.description}</VideoDescription>
                        {video.link.includes("youtube") ? (
                          <VideoIframe
                            src={video.link}
                            title={`${video.name} - YouTube video player`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        ) : (
                          <VideoElement
                            controls
                            src={video.link}
                            poster={video?.poster}
                          >
                            Your browser does not support the video tag.
                          </VideoElement>
                        )}
                      </VideoCard>
                    ))}
                  </VideoGrid>
                </ContentColumn>
              </div>
            </SectionContainer>
          </ContentSection>
        </>
      )}

      {/* PROJECTS Section - with animation */}
      {/* <ContentSection ref={projectsRef}>
        <SectionContainer>
          <TwoColumnLayout>
            <SectionHeading>Projects</SectionHeading>
            <ContentColumn>
              <div
                style={{
                  width: "100%",
                  marginTop: "2rem",
                  marginBottom: "3rem",
                }}
              >
                <ProjectCards3D
                  projects={[
                    {
                      category: "AI/ML",
                      title: "AI Project 1",
                      image: profile,
                    },
                    {
                      category: "Web Development",
                      title: "Web Project 1",
                      image: profile,
                    },
                    {
                      category: "Research",
                      title: "Research Project 1",
                      image: profile,
                    },
                    {
                      category: "Education",
                      title: "Education Project 1",
                      image: profile,
                    },
                  ]}
                />
              </div>
              <div style={{ width: "100%", marginTop: "3rem" }}>
                <h3
                  style={{
                    color: "#d4af37",
                    marginBottom: "1rem",
                    fontSize: "1.25rem",
                    fontFamily:
                      "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', sans-serif",
                  }}
                >
                  Alternative Style (Carousel)
                </h3>
                <ProjectCardsCarousel
                  projects={[
                    {
                      category: "AI/ML",
                      title: "AI Project 1",
                      image: profile,
                    },
                    {
                      category: "Web Development",
                      title: "Web Project 1",
                      image: profile,
                    },
                    {
                      category: "Research",
                      title: "Research Project 1",
                      image: profile,
                    },
                    {
                      category: "Education",
                      title: "Education Project 1",
                      image: profile,
                    },
                    {
                      category: "AI/ML",
                      title: "AI Project 2",
                      image: profile,
                    },
                    {
                      category: "Web Development",
                      title: "Web Project 2",
                      image: profile,
                    },
                    {
                      category: "Research",
                      title: "Research Project 2",
                      image: profile,
                    },
                    {
                      category: "Education",
                      title: "Education Project 2",
                      image: profile,
                    },
                  ]}
                />
              </div>
            </ContentColumn>
          </TwoColumnLayout>
        </SectionContainer>
      </ContentSection> */}

      {/* Scroll to Top Button */}
      {/* NEWS Section (moved to end) */}
      <ContentSection ref={newsRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <SectionHeading
              style={{ marginBottom: "1.25rem", textAlign: "left" }}
            >
              <TextScramble>News</TextScramble>
            </SectionHeading>
            <ContentColumn>
              {newsItems.map((item) => (
                <PublicationItem key={`${item.outlet}-${item.date}-${item.href}`}>
                  <ConferenceColumn>
                    <ConferenceBadge>{item.outlet}</ConferenceBadge>
                  </ConferenceColumn>
                  <PublicationContent>
                    <ExperienceDescription>
                      <strong>{item.date}</strong>. {item.description}
                    </ExperienceDescription>
                    <div style={{ marginTop: "0.5rem" }}>
                      <SectionLink
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.linkLabel}
                        <OpenInNewIcon />
                      </SectionLink>
                    </div>
                  </PublicationContent>
                </PublicationItem>
              ))}
            </ContentColumn>
          </div>
        </SectionContainer>
      </ContentSection>

      {/* PROJECTS Section */}
      <ContentSection ref={projectsRef}>
        <SectionContainer>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            {(() => {
              // Calculate date range from projects
              const sortedProjects = [...allProjectsData].sort(
                (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
              );
              const firstYear = new Date(
                sortedProjects[0].timestamp
              ).getFullYear();
              const lastYear = new Date(
                sortedProjects[sortedProjects.length - 1].timestamp
              ).getFullYear();
              const yearRange =
                firstYear === lastYear
                  ? firstYear.toString()
                  : `${firstYear}–${lastYear}`;

              return (
                <>
                  <SectionHeading
                    style={{ marginBottom: "0.75rem", textAlign: "left" }}
                  >
                    <TextScramble>Past Projects ({yearRange})</TextScramble>
                  </SectionHeading>
                  <CompactProjectIntro>
                    All projects from my portfolio app:{" "}
                    <strong>{allProjectsData.length} projects</strong> (
                    {yearRange}).
                  </CompactProjectIntro>
                  <CompactProjectList>
                    {allProjectsData.map((project, idx) => {
                      const githubUrl = `https://github.com/charliechenyuzhang/${project.github_path}`;
                      const displayName =
                        project.vanity_name || project.github_path || "Project";

                      return (
                        <CompactProjectItem key={idx}>
                          <CompactProjectBullet aria-hidden="true">
                            •
                          </CompactProjectBullet>
                          {project.icon && (
                            <CompactProjectIcon aria-hidden="true">
                              {project.icon}
                            </CompactProjectIcon>
                          )}
                          <ProjectName
                            href={githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {displayName}
                          </ProjectName>
                          {project.short_description && (
                            <>
                              <ProjectSummarySeparator> - </ProjectSummarySeparator>
                              <ProjectShortDescription>
                                {project.short_description}
                              </ProjectShortDescription>
                            </>
                          )}
                        </CompactProjectItem>
                      );
                    })}
                  </CompactProjectList>
                </>
              );
            })()}
          </div>
        </SectionContainer>
      </ContentSection>

      {/* Scroll to Top Button */}
      <ScrollToTopButton
        visible={scrollProgress > 20}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        ↑
      </ScrollToTopButton>
    </MainContainer>
  );
};

export default SinglePageApp;
